import { AESDecrypt, AESEncrypt } from './utils';

/**
 * 存储操作工具类
 * 封装对 sessionStorage 和 localStorage 的操作，提供统一的存储接口
 */
export class StorageUtil {
  /**
   * 存储数据到 sessionStorage
   * @param key 存储键名
   * @param value 存储值
   * @param encrypt 是否加密存储
   */
  static setSession<T>(key: string, value: T, encrypt: boolean = true): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 sessionStorage');
      return;
    }
    try {
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      const storedValue = encrypt ? AESEncrypt(data) : data;
      sessionStorage.setItem(key, storedValue);
    } catch (error) {
      console.error('存储到 sessionStorage 失败:', error);
    }
  }

  /**
   * 从 sessionStorage 获取数据
   * @param key 存储键名
   * @param decrypt 是否解密数据
   * @returns 存储的值，若不存在则返回 null
   */
  static getSession<T>(key: string, decrypt: boolean = true): T | null {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 sessionStorage');
      return null;
    }
    try {
      const storedValue = sessionStorage.getItem(key);
      if (!storedValue) return null;

      const data = decrypt ? AESDecrypt(storedValue) : storedValue;
      try {
        return JSON.parse(data) as T;
      } catch {
        return data as unknown as T;
      }
    } catch (error) {
      console.error('从 sessionStorage 获取数据失败:', error);
      return null;
    }
  }

  /**
   * 从 sessionStorage 删除数据
   * @param key 存储键名
   */
  static removeSession(key: string): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 sessionStorage');
      return;
    }
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('从 sessionStorage 删除数据失败:', error);
    }
  }

  /**
   * 清空 sessionStorage
   */
  static clearSession(): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 sessionStorage');
      return;
    }
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('清空 sessionStorage 失败:', error);
    }
  }

  /**
   * 存储数据到 localStorage
   * @param key 存储键名
   * @param value 存储值
   * @param encrypt 是否加密存储
   */
  static setLocal<T>(key: string, value: T, encrypt: boolean = true): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 localStorage');
      return;
    }
    try {
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      const storedValue = encrypt ? AESEncrypt(data) : data;
      localStorage.setItem(key, storedValue);
    } catch (error) {
      console.error('存储到 localStorage 失败:', error);
    }
  }

  /**
   * 从 localStorage 获取数据
   * @param key 存储键名
   * @param decrypt 是否解密数据
   * @returns 存储的值，若不存在则返回 null
   */
  static getLocal<T>(key: string, decrypt: boolean = true): T | null {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 localStorage');
      return null;
    }
    try {
      const storedValue = localStorage.getItem(key);
      if (!storedValue) return null;

      const data = decrypt ? AESDecrypt(storedValue) : storedValue;
      try {
        return JSON.parse(data) as T;
      } catch {
        return data as unknown as T;
      }
    } catch (error) {
      console.error('从 localStorage 获取数据失败:', error);
      return null;
    }
  }

  /**
   * 从 localStorage 删除数据
   * @param key 存储键名
   */
  static removeLocal(key: string): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 localStorage');
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('从 localStorage 删除数据失败:', error);
    }
  }

  /**
   * 清空 localStorage
   */
  static clearLocal(): void {
    if (typeof window === "undefined") {
      console.warn('在服务器端无法使用 localStorage');
      return;
    }
    try {
      localStorage.clear();
    } catch (error) {
      console.error('清空 localStorage 失败:', error);
    }
  }
}

/**
 * 用户信息类型定义
 */
export interface StoredUserInfo {
  id: string;
  userName: string;
  tenantId: string;
  email?: string;
  avatar?: string;
  roleName?: string;
  [key: string]: any;
}

/**
 * 用户信息存储工具
 */
export const UserStorage = {
  /**
   * 存储用户信息
   */
  setUserInfo(userInfo: StoredUserInfo): void {
    StorageUtil.setSession<StoredUserInfo>('userInfo', userInfo);
  },

  /**
   * 获取用户信息
   */
  getUserInfo(): StoredUserInfo | null {
    return StorageUtil.getSession<StoredUserInfo>('userInfo');
  },

  /**
   * 清除用户信息
   */
  clearUserInfo(): void {
    StorageUtil.removeSession('userInfo');
  },

  /**
   * 存储会话 ID
   */
  setSessionId(sessionId: string): void {
    StorageUtil.setSession<string>('sessionId', sessionId, false);
  },

  /**
   * 获取会话 ID
   */
  getSessionId(): string | null {
    return StorageUtil.getSession<string>('sessionId', false);
  },

  /**
   * 清除会话 ID
   */
  clearSessionId(): void {
    StorageUtil.removeSession('sessionId');
  },

  /**
   * 完全清除用户登录状态
   */
  clearAuth(): void {
    this.clearUserInfo();
    this.clearSessionId();
  },
};